import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSectionHeader } from '../../../../../AnalysisSectionHeader'
import {
  BuildingDetailsContainer,
  BuildingContentContainer,
  LegendContainer,
  LegendItem,
  LegendDot,
  LegendSwatch,
} from './BuildingDetailsTab.styles'
import { SummaryBar, BuildingsList } from './sections'
import { processBuildings } from './utils/buildingData.utils'
import { useAnalysisMode } from '../../../contexts/AnalysisModeContext'
import { useAnalysisMapSectionData } from '../../../contexts/MapSectionDataContext'

const getScoreVariant = (score: number): 'success' | 'warning' | 'danger' | 'default' => {
  if (score >= 80) return 'success'
  if (score >= 60) return 'default'
  if (score >= 40) return 'warning'
  return 'danger'
}

/**
 * Shared BuildingDetailsTab for read-only display of building details.
 * Used by public pages (land analysis).
 * Reads entity data from AnalysisModeContext.
 */
export const BuildingDetailsTab: React.FC = () => {
  const { t } = useTranslation('land-analysis')
  const { entity } = useAnalysisMode()
  const { setActiveSection, clearSectionData } = useAnalysisMapSectionData()

  const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(new Set(['building-0']))

  // Clear map section data on mount (building details has no map layers)
  useEffect(() => {
    setActiveSection(null)
    clearSectionData()
  }, [setActiveSection, clearSectionData])

  if (!entity) {
    return (
      <BuildingDetailsContainer>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          {t('buildingDetails.states.loading')}
        </div>
      </BuildingDetailsContainer>
    )
  }

  const formatAddress = () => {
    if (entity.address_full) return entity.address_full
    if (entity.address_street && entity.address_postal_code) {
      return `${entity.address_street} ${entity.address_street_nb || ''}, ${
        entity.address_postal_code
      }`.trim()
    }
    return entity.municipality_name || t('buildingDetails.states.unknownLocation')
  }

  const toggleBuilding = (buildingId: string) => {
    const newExpanded = new Set(expandedBuildings)
    if (newExpanded.has(buildingId)) {
      newExpanded.delete(buildingId)
    } else {
      newExpanded.add(buildingId)
    }
    setExpandedBuildings(newExpanded)
  }

  const buildings = processBuildings(entity)

  return (
    <BuildingDetailsContainer>
      <AnalysisSectionHeader
        title={t('buildingDetails.header.title')}
        subtitle={t('buildingDetails.header.subtitle', {
          address: formatAddress(),
        })}
        score={
          entity.existing_score
            ? {
                value: entity.existing_score,
                maxValue: 100,
                label: t('buildingDetails.header.scoreLabel'),
                variant: getScoreVariant(entity.existing_score),
                showProgress: true,
              }
            : undefined
        }
      />
      <BuildingContentContainer>
        <SummaryBar buildings={buildings} />
        <BuildingsList
          buildings={buildings}
          expandedBuildings={expandedBuildings}
          onToggleBuilding={toggleBuilding}
        />
        <LegendContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <LegendItem>
              <LegendSwatch $color="#0294df" />
              {t('buildingDetails.legend.estimatedValues')}
            </LegendItem>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px 8px',
              }}
            >
              <LegendItem>
                <LegendDot $color="#0294df" />
                {t('buildingDetails.legend.residential')}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#ff7a00" />
                {t('buildingDetails.legend.residentialMinority')}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#007aa6" />
                {t('buildingDetails.legend.residentialMajority')}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#ff9500" />
                {t('buildingDetails.legend.retail')}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#7b61ff" />
                {t('buildingDetails.legend.offices')}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#ff3b30" />
                {t('buildingDetails.legend.industrial')}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#00b341" />
                {t('buildingDetails.legend.public')}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#00aab8" />
                {t('buildingDetails.legend.garage')}
              </LegendItem>
              <LegendItem>
                <LegendDot $color="#6b6b6b" />
                {t('buildingDetails.legend.other')}
              </LegendItem>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 8px' }}>
              <LegendItem>
                <LegendSwatch $color="rgba(0, 179, 65, 0.15)" />
                {t('buildingDetails.legend.fullCoverage')}
              </LegendItem>

              <LegendItem>
                <LegendSwatch $color="rgba(255, 59, 48, 0.15)" />
                {t('buildingDetails.legend.partialCoverage')}
              </LegendItem>
            </div>
          </div>
        </LegendContainer>
      </BuildingContentContainer>
    </BuildingDetailsContainer>
  )
}
